import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Star, ThumbsUp, ThumbsDown } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const RateRide = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { driver, rideData } = location.state || {};
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [tip, setTip] = useState(0);

  const tipOptions = [2, 5, 10, 15];

  const handleSubmit = async () => {
    if (rating === 0) {
      toast({
        title: 'Rating required',
        description: 'Please rate your ride before submitting',
        variant: 'destructive',
      });
      return;
    }

    try {
      const rideId = localStorage.getItem('current_ride_id');
      const api = (await import('../utils/api')).default;
      
      await api.post(`/rides/${rideId}/rate`, {
        rating,
        feedback,
        tip,
      });

      toast({
        title: 'Thank you!',
        description: 'Your feedback has been submitted',
      });

      // Clear current ride
      localStorage.removeItem('current_ride_id');

      setTimeout(() => {
        navigate('/rides');
      }, 1000);
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit rating. Please try again.',
        variant: 'destructive',
      });
    }
  };

  if (!driver || !rideData) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-8">
      <div className="max-w-2xl mx-auto px-4">
        <Card className="p-8">
          <div className="text-center space-y-6">
            {/* Driver info */}
            <div className="space-y-4">
              <img 
                src={driver.photo} 
                alt={driver.name}
                className="w-24 h-24 rounded-full mx-auto bg-gray-200"
              />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Rate your ride</h2>
                <p className="text-gray-600">How was your trip with {driver.name}?</p>
              </div>
            </div>

            {/* Star rating */}
            <div className="flex justify-center gap-2 py-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    className={`h-12 w-12 ${
                      star <= (hoveredRating || rating)
                        ? 'text-yellow-500 fill-yellow-500'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>

            {rating > 0 && (
              <div className="text-center">
                <p className="text-lg font-medium text-gray-700">
                  {rating === 5 && 'Excellent!'}
                  {rating === 4 && 'Great!'}
                  {rating === 3 && 'Good'}
                  {rating === 2 && 'Okay'}
                  {rating === 1 && 'Not great'}
                </p>
              </div>
            )}

            {/* Feedback */}
            <div className="text-left space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Additional feedback (optional)
              </label>
              <Textarea
                placeholder="Share more about your experience..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                className="min-h-24"
              />
            </div>

            {/* Tip options */}
            <div className="text-left space-y-3">
              <label className="text-sm font-medium text-gray-700">
                Add a tip for {driver.name}?
              </label>
              <div className="grid grid-cols-5 gap-2">
                {tipOptions.map((amount) => (
                  <Button
                    key={amount}
                    variant={tip === amount ? 'default' : 'outline'}
                    className={tip === amount ? 'bg-pink-600 hover:bg-pink-700' : ''}
                    onClick={() => setTip(amount)}
                  >
                    K{amount}
                  </Button>
                ))}
                <Button
                  variant={tip === 0 ? 'default' : 'outline'}
                  className={tip === 0 ? 'bg-gray-600 hover:bg-gray-700' : ''}
                  onClick={() => setTip(0)}
                >
                  No tip
                </Button>
              </div>
            </div>

            {/* Submit */}
            <div className="pt-4">
              <Button
                onClick={handleSubmit}
                className="w-full bg-pink-600 hover:bg-pink-700 text-white py-6 text-lg font-semibold"
              >
                Submit rating
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default RateRide;